// ============================================
// Reset Password Page — Premium & Bulletproof
// URL: /reset-password/:token
// Validates token ON LOAD — expired links show
// immediate expiry screen, not the form.
// ============================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const ResetPasswordPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { theme, toggleTheme } = useTheme();

    // Read role from URL query param (?role=driver or ?role=pa)
    // This is embedded in the reset link by the backend, so it survives
    // even after the token expires.
    const queryParams = new URLSearchParams(location.search);
    const urlRole = queryParams.get('role') || 'driver';
    const roleLabels = {
        driver: 'Driver Portal',
        pa: 'PA (Passenger Assistant) Portal',
        user: 'User Portal',
        admin: 'Admin Portal',
        dispatcher: 'Dispatcher Portal',
    };

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // 'validating' = checking token on load
    // 'valid'      = token is valid, show form
    // 'expired'    = token is invalid/expired, show error screen (no form)
    // 'submitting' = form is being submitted
    // 'success'    = password reset successfully
    // 'error'      = form submission error (but token was valid)
    const [pageState, setPageState] = useState('validating');
    const [errorMessage, setErrorMessage] = useState('');
    const [portalName, setPortalName] = useState('');

    // ── Validate token on page mount ─────────────────
    useEffect(() => {
        const validateToken = async () => {
            try {
                const { data } = await api.get(`/auth/verify-reset-token/${token}`);
                // Use portalName from API; fall back to urlRole if API doesn't return it
                setPortalName(data.portalName || roleLabels[urlRole] || '');
                setPageState('valid');
            } catch (err) {
                const msg = err.response?.data?.message || 'Invalid or expired token';
                setErrorMessage(msg);
                // Even on expiry, show the correct portal name from URL param
                setPortalName(roleLabels[urlRole] || '');
                setPageState('expired');
            }
        };
        validateToken();
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    const passwordRules = [
        { test: password.length >= 8, label: '8+ Characters' },
        { test: /[A-Z]/.test(password), label: 'Uppercase (A-Z)' },
        { test: /[a-z]/.test(password), label: 'Lowercase (a-z)' },
        { test: /[0-9]/.test(password), label: 'Number (0-9)' },
        { test: /[!@#$%^&*(),.?":{}|<>]/.test(password), label: 'Symbol (!@#$%)' },
    ];
    const strengthScore = passwordRules.filter(r => r.test).length;
    const strengthBar = strengthScore <= 2 ? 'bg-red-500 w-[20%]' : strengthScore <= 3 ? 'bg-amber-500 w-[50%]' : strengthScore <= 4 ? 'bg-teal-500 w-[75%]' : 'bg-emerald-500 w-full';

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            setErrorMessage('Passwords do not match.');
            setPageState('error');
            return;
        }
        if (strengthScore < 5) {
            setErrorMessage('Password must be at least 8 characters with uppercase, lowercase, number, and special character.');
            setPageState('error');
            return;
        }

        setPageState('submitting');
        setErrorMessage('');
        try {
            await api.post(`/auth/reset-password/${token}`, { password });
            setPageState('success');
            setTimeout(() => navigate('/login'), 4000);
        } catch (err) {
            const msg = err.response?.data?.message || 'Reset failed. The link may have expired.';
            setErrorMessage(msg);
            // If it's a token error, show the expired screen
            if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('invalid')) {
                setPageState('expired');
            } else {
                setPageState('error');
            }
        }
    };

    // ── Shared layout wrapper ──────────────────────────
    const Layout = ({ children }) => (
        <div className="h-screen w-screen flex bg-slate-50 dark:bg-[#050510] overflow-hidden font-['Inter',sans-serif] transition-colors duration-500">
            {/* Left Panel */}
            <div className="hidden lg:flex lg:w-[45%] xl:w-[50%] relative group overflow-hidden">
                <div className="absolute inset-0 bg-slate-900 dark:bg-black transition-colors duration-500">
                    <img src="/assets/login-bg.png" alt="IdaCab Premium Transport"
                        className="w-full h-full object-cover opacity-[0.85] dark:opacity-40 grayscale-[10%] group-hover:scale-105 transition-transform duration-[20s] ease-out mix-blend-overlay" />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
                </div>
                <div className="relative z-10 flex flex-col justify-between p-16 w-full h-full">
                    <div className="flex items-start">
                        <div className="bg-white/95 p-1.5 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] inline-flex items-center justify-center backdrop-blur-sm">
                            <img src="/assets/logo.png" alt="IdaCab Logo" className="h-16 sm:h-24 w-auto object-contain" />
                        </div>
                    </div>
                    <div className="max-w-lg mb-12">
                        <h1 className="text-5xl lg:text-6xl font-black text-white leading-[1.1] mb-6 tracking-tight drop-shadow-2xl">
                            Secure<span className="block text-teal-400">Password Reset</span>
                        </h1>
                        <p className="text-white/90 text-lg leading-relaxed font-medium">
                            Your account security is our top priority. Links expire in 10 minutes for your protection.
                        </p>
                    </div>
                    <div className="flex items-center gap-6 text-white/50 text-[11px] font-black tracking-widest uppercase">
                        <span>End-to-End Encrypted</span>
                        <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                        <span>10 Min Expiry</span>
                    </div>
                </div>
            </div>

            {/* Right Panel */}
            <div className="flex-1 h-full relative selection:bg-teal-500/20 z-10 flex flex-col justify-center items-center p-4 sm:p-8 lg:p-20 overflow-hidden">
                {/* Theme Toggle */}
                <div className="absolute top-6 right-6 z-50">
                    <button onClick={toggleTheme}
                        className="p-3 rounded-full bg-white/50 dark:bg-slate-800/50 backdrop-blur-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all border border-slate-200 dark:border-slate-700 hover:scale-105 active:scale-95 shadow-sm"
                        aria-label="Toggle theme">
                        {theme === 'light' ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
                        ) : (
                            <svg className="w-5 h-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                        )}
                    </button>
                </div>

                <div className="w-full max-w-[460px] min-w-0 overflow-x-hidden scrollbar-hide bg-white/80 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] p-6 sm:p-8 shadow-2xl shadow-slate-200 dark:shadow-black/50 border border-white/60 dark:border-white/5 transition-colors duration-500 relative z-10">

                    {/* Logo (mobile) */}
                    <div className="mb-6 flex flex-col items-center lg:hidden">
                        <div className="bg-white/95 p-1.5 rounded-2xl shadow-lg inline-flex items-center justify-center">
                            <img src="/assets/logo.png" alt="IdaCab Logo" className="h-16 w-auto object-contain" />
                        </div>
                    </div>

                    {children}
                </div>
            </div>
        </div>
    );

    // ── State: Validating (loading) ────────────────
    if (pageState === 'validating') {
        return (
            <Layout>
                <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
                    <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm font-bold text-slate-500 dark:text-slate-400">Verifying your reset link…</p>
                </div>
            </Layout>
        );
    }

    // ── State: Expired / Invalid Token ─────────────
    if (pageState === 'expired') {
        return (
            <Layout>
                <div className="text-center space-y-6 py-4">
                    {/* Icon */}
                    <div className="mx-auto w-20 h-20 rounded-full bg-rose-100 dark:bg-rose-500/20 flex items-center justify-center">
                        <svg className="w-10 h-10 text-rose-600 dark:text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>

                    <div>
                        <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">Link Expired</h2>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium leading-relaxed">
                            This password reset link has expired or has already been used.<br />
                            Reset links are valid for <strong className="text-rose-600 dark:text-rose-400">10 minutes only</strong>.
                        </p>
                    </div>

                    {/* Error detail */}
                    <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-left">
                        <p className="text-rose-700 dark:text-rose-400 text-xs font-bold uppercase tracking-wide leading-relaxed">{errorMessage}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-3 pt-2">
                        <Link to={`/forgot-password?role=${urlRole}`}
                            className="w-full py-4 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white text-[11px] font-black uppercase tracking-[0.2em] shadow-lg shadow-teal-500/20 transition-all text-center">
                            Request a New Reset Link
                        </Link>
                        <Link to="/login"
                            className="w-full py-3.5 rounded-2xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-[11px] font-black uppercase tracking-[0.2em] transition-all text-center">
                            Back to Login
                        </Link>
                    </div>
                </div>
            </Layout>
        );
    }

    // ── State: Success ─────────────────────────────
    if (pageState === 'success') {
        return (
            <Layout>
                <div className="text-center space-y-6 py-4">
                    <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
                        <svg className="w-10 h-10 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">Password Changed!</h2>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium leading-relaxed">
                            Your password has been reset successfully. You can now sign in with your new credentials.
                        </p>
                        {portalName && (
                            <div className="mt-3 inline-block px-4 py-1.5 rounded-full bg-teal-50 dark:bg-teal-500/10 border border-teal-200 dark:border-teal-500/30">
                                <p className="text-teal-700 dark:text-teal-400 text-[10px] font-black uppercase tracking-widest">{portalName}</p>
                            </div>
                        )}
                    </div>
                    <div className="pt-2">
                        <p className="text-xs text-slate-400 dark:text-slate-500 mb-4">Redirecting to login in 4 seconds…</p>
                        <Link to="/login" className="w-full py-4 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white text-[11px] font-black uppercase tracking-[0.2em] shadow-lg shadow-teal-500/20 transition-all text-center block">
                            Go to Login Now
                        </Link>
                    </div>
                </div>
            </Layout>
        );
    }

    // ── State: Valid / Form / Error ────────────────
    return (
        <Layout>
            {/* Header */}
            <div className="text-center mb-6">
                <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-1">Set New Password</h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Enter your new secure password below</p>
                {portalName && (
                    <div className="mt-3 inline-block px-4 py-1.5 rounded-full bg-teal-50 dark:bg-teal-500/10 border border-teal-200 dark:border-teal-500/30">
                        <p className="text-teal-700 dark:text-teal-400 text-[10px] font-black uppercase tracking-widest">{portalName}</p>
                    </div>
                )}
            </div>

            {/* Error Banner (form-level) */}
            {pageState === 'error' && (
                <div className="mb-5 flex items-start gap-3 px-4 py-4 rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30">
                    <div className="w-8 h-8 rounded-xl bg-rose-100 dark:bg-rose-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-rose-600 dark:text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-rose-700 dark:text-rose-300">Error</p>
                        <p className="text-[13px] text-rose-600 dark:text-rose-400 mt-0.5 leading-relaxed">{errorMessage}</p>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* New Password */}
                <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.15em] ml-1">New Password</label>
                    <div className="relative">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            required value={password} onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:border-teal-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all text-sm font-medium shadow-sm pr-16"
                            placeholder="••••••••"
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-5 flex items-center text-[10px] font-black text-slate-400 uppercase hover:text-teal-500 tracking-widest">
                            {showPassword ? 'Hide' : 'Show'}
                        </button>
                    </div>

                    {/* Strength indicator */}
                    {password.length > 0 && (
                        <div className="mt-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/50 transition-colors">
                            <div className="grid grid-cols-2 gap-1.5">
                                {passwordRules.map((rule, i) => (
                                    <div key={i} className={`flex items-center gap-1.5 text-[10px] font-bold transition-colors ${rule.test ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}>
                                        {rule.test ? (
                                            <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                        ) : (
                                            <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                                        )}
                                        {rule.label}
                                    </div>
                                ))}
                            </div>
                            <div className="mt-2 h-1 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                <div className={`h-full rounded-full transition-all duration-300 ${strengthBar}`} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.15em] ml-1">Confirm Password</label>
                    <input
                        type={showPassword ? 'text' : 'password'}
                        required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border focus:ring-4 outline-none transition-all text-sm font-medium shadow-sm
                            ${confirmPassword.length > 0 && confirmPassword !== password
                                ? 'border-rose-400 dark:border-rose-500 focus:border-rose-400 focus:ring-rose-500/10 text-rose-700 dark:text-rose-400'
                                : 'border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:border-teal-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-teal-500/10'
                            }`}
                        placeholder="••••••••"
                    />
                    {confirmPassword.length > 0 && confirmPassword !== password && (
                        <p className="text-[10px] text-rose-500 font-bold ml-1">Passwords do not match</p>
                    )}
                </div>

                <button type="submit"
                    disabled={pageState === 'submitting' || (confirmPassword.length > 0 && confirmPassword !== password)}
                    className="w-full py-4 mt-2 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white text-[11px] font-black uppercase tracking-[0.2em] shadow-lg shadow-teal-500/20 active:scale-[0.98] disabled:opacity-50 disabled:grayscale transition-all flex justify-center items-center h-[52px]">
                    {pageState === 'submitting'
                        ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : <span>Reset Password</span>
                    }
                </button>
            </form>

            <div className="mt-6 text-center">
                <Link to="/login" className="text-[10px] font-black text-slate-400 dark:text-slate-500 hover:text-teal-600 dark:hover:text-teal-400 uppercase tracking-widest transition-colors">
                    Back to Login
                </Link>
            </div>
        </Layout>
    );
};

export default ResetPasswordPage;
